// Pure rules about what the rows found for a template name mean. Kept free of
// Apollo/React imports so any form can use them (and tests can mount the
// forms) without loading the GraphQL client. `api/queries/use-check-template-name`
// re-exports everything here next to the lookup that feeds it.

// WhatsApp keeps the name of a deleted template locked for a while (up to 30
// days when the template had been approved). The backend keeps the row, marked
// `status: "deleted"`, and never removes it, so the lock is counted from the
// row's `updatedAt`.
export const TEMPLATE_NAME_DELETED_LOCK_MS = 30 * 24 * 60 * 60 * 1000;

export const TEMPLATE_NAME_DELETED_MESSAGE =
  "Una plantilla con este nombre se eliminó hace poco en WhatsApp, y WhatsApp bloquea su nombre hasta por 30 días. Usa otro nombre.";

/**
 * What the rows returned for a name mean for creating a template with it:
 *  - "taken": some row is not deleted (any other status, or none at all), so
 *    the provider rejects the name.
 *  - "deleted_recently": every row is deleted and at least one was deleted
 *    less than TEMPLATE_NAME_DELETED_LOCK_MS before `nowMs`: the provider still
 *    locks the name.
 *  - "available": no rows, or only rows deleted long ago. A missing or
 *    unparseable `updatedAt` does not block either (same fail-open spirit as
 *    the lookup); the provider's own rejection is mapped to a clear message
 *    elsewhere.
 */
export const classifyTemplateNameMatches = (matches, nowMs = Date.now()) => {
  if (!Array.isArray(matches) || matches.length === 0) return "available";
  if (matches.some((match) => match?.status !== "deleted")) return "taken";

  const lockedRecently = matches.some((match) => {
    const updatedAtMs = new Date(match.updatedAt).getTime();
    return (
      Number.isFinite(updatedAtMs) &&
      nowMs - updatedAtMs < TEMPLATE_NAME_DELETED_LOCK_MS
    );
  });
  return lockedRecently ? "deleted_recently" : "available";
};
